import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Alert,
    AlertTitle,
    Snackbar,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { TenantList, TenantForm, TenantDetails, TenantDeleteConfirmation } from '../components';
import { TenantApiService } from '../services/tenant-api';
import type { TenantWithStatus, TenantStatus, CreateTenant, UpdateTenant, Notification } from '../types';

export function TenantsPage() {
    const [tenants, setTenants] = useState<TenantWithStatus[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<TenantStatus | 'all'>('all');
    const [hasMore, setHasMore] = useState(false);
    const [nextToken, setNextToken] = useState<string | undefined>();

    // Form state
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState<TenantWithStatus | null>(null);
    const [formLoading, setFormLoading] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [notification, setNotification] = useState<Notification | null>(null);

    // Initialize API service (you'll need to provide proper config)
    const tenantApi = new TenantApiService();

    const loadTenants = async (reset = false) => {
        try {
            if (reset) {
                setLoading(true);
                setTenants([]);
                setNextToken(undefined);
            } else {
                setLoadingMore(true);
            }
            setError(null);

            const response = await tenantApi.getTenants({
                limit: 20,
                nextToken: reset ? undefined : nextToken,
            });

            if (reset) {
                setTenants(response.items);
            } else {
                setTenants((t) => [...t, ...response.items]);
            }

            setHasMore(!!response.nextToken);
            setNextToken(response.nextToken);
        } catch (err) {
            console.error('Failed to load tenants:', err);
            setError(err instanceof Error ? err.message : 'Failed to load tenants');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleSearch = () => {
        loadTenants(true);
    };

    const handleRefresh = () => {
        loadTenants(true);
    };

    const handleLoadMore = () => {
        if (hasMore && !loadingMore) {
            loadTenants(false);
        }
    };

    const handleViewDetails = async (tenant: TenantWithStatus) => {
        setSelectedTenant(tenant);
        setDetailsLoading(true);
        setShowDetailsModal(true);

        try {
            // Fetch fresh tenant details
            const freshTenant = await tenantApi.getTenant(tenant.tenantId);
            setSelectedTenant(freshTenant);
        } catch (error) {
            console.error('Failed to load tenant details:', error);
            setNotification({
                id: Date.now().toString(),
                type: 'error',
                title: 'Error Loading Details',
                message: 'Failed to load tenant details. Please try again.',
                duration: 5000,
            });
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleEdit = (tenant: TenantWithStatus) => {
        setSelectedTenant(tenant);
        setShowEditForm(true);
    };

    const handleDelete = (tenant: TenantWithStatus) => {
        setSelectedTenant(tenant);
        setShowDeleteConfirmation(true);
    };

    const handleDeleteConfirm = async (tenantId: string) => {
        if (!selectedTenant) return;

        setDeleteLoading(true);
        try {
            // Call the API to delete the tenant
            await tenantApi.deleteTenant(tenantId);

            // Remove the tenant from the list
            setTenants((prev) => prev.filter((t) => t.tenantId !== tenantId));

            // Close the confirmation dialog
            setShowDeleteConfirmation(false);
            setSelectedTenant(null);

            // Show success notification
            setNotification({
                id: Date.now().toString(),
                type: 'success',
                title: 'Tenant Deleted',
                message: `Tenant "${selectedTenant.name}" has been deleted successfully.`,
                duration: 5000,
            });
        } catch (error) {
            console.error('Failed to delete tenant:', error);

            // Show error notification
            setNotification({
                id: Date.now().toString(),
                type: 'error',
                title: 'Deletion Failed',
                message: error instanceof Error ? error.message : 'Failed to delete tenant. Please try again.',
                duration: 8000,
            });

            // Re-throw the error so the confirmation dialog can handle it
            throw error;
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirmation(false);
        setSelectedTenant(null);
    };

    const handleCreateTenant = () => {
        setShowCreateForm(true);
    };

    const handleFormSubmit = async (data: CreateTenant | UpdateTenant) => {
        setFormLoading(true);
        try {
            if (showEditForm && selectedTenant) {
                // Update existing tenant
                const updatedTenant = await tenantApi.updateTenant(selectedTenant.tenantId, data as UpdateTenant);

                // Update the tenant in the list
                setTenants((prev) => prev.map((t) => (t.tenantId === selectedTenant.tenantId ? updatedTenant : t)));

                // Close the form
                setShowEditForm(false);
                setSelectedTenant(null);

                // Show success notification
                setNotification({
                    id: Date.now().toString(),
                    type: 'success',
                    title: 'Tenant Updated',
                    message: `Tenant "${updatedTenant.name}" has been updated successfully.`,
                    duration: 5000,
                });
            } else {
                // Create new tenant
                const newTenant = await tenantApi.createTenant(data as CreateTenant);

                // Add the new tenant to the beginning of the list
                setTenants((prev) => [newTenant, ...prev]);

                // Close the form
                setShowCreateForm(false);

                // Show success notification
                setNotification({
                    id: Date.now().toString(),
                    type: 'success',
                    title: 'Tenant Created',
                    message: `Tenant "${newTenant.name}" has been created successfully.`,
                    duration: 5000,
                });
            }
        } catch (error) {
            console.error('Failed to save tenant:', error);
            // Error will be handled by the form component
            throw error;
        } finally {
            setFormLoading(false);
        }
    };

    const handleFormCancel = () => {
        setShowCreateForm(false);
        setShowEditForm(false);
        setSelectedTenant(null);
    };

    const handleDetailsClose = () => {
        setShowDetailsModal(false);
        setSelectedTenant(null);
    };

    const handleEditFromDetails = (tenant: TenantWithStatus) => {
        setShowDetailsModal(false);
        setSelectedTenant(tenant);
        setShowEditForm(true);
    };

    const handleValidateSubdomain = async (subdomain: string) => {
        try {
            const result = await tenantApi.validateSubdomain(subdomain);
            return result;
        } catch (error) {
            console.error('Failed to validate subdomain:', error);
            return { available: false, message: 'Failed to validate subdomain' };
        }
    };

    const handleCloseNotification = () => {
        setNotification(null);
    };

    useEffect(() => {
        loadTenants(true);
    }, []);

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h1" gutterBottom>
                    Tenant Management
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Manage all tenant accounts and configurations
                </Typography>
            </Box>

            {/* Search and Filter Controls */}
            <Box sx={{ mb: 3 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <TextField
                        placeholder="Search tenants..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{ flexGrow: 1, minWidth: 300 }}
                    />

                    <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value as TenantStatus | 'all')}>
                            <MenuItem value="all">All Statuses</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="provisioning">Provisioning</MenuItem>
                            <MenuItem value="error">Error</MenuItem>
                        </Select>
                    </FormControl>

                    <Button variant="outlined" onClick={handleSearch} startIcon={<SearchIcon />}>
                        Search
                    </Button>

                    <Button variant="outlined" onClick={handleRefresh} startIcon={<RefreshIcon />}>
                        Refresh
                    </Button>

                    <Button variant="contained" onClick={handleCreateTenant} startIcon={<AddIcon />}>
                        Create Tenant
                    </Button>
                </Stack>
            </Box>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    <AlertTitle>Error</AlertTitle>
                    {error}
                </Alert>
            )}

            {/* Tenant List */}
            <TenantList
                tenants={tenants}
                loading={loading}
                loadingMore={loadingMore}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onViewDetails={handleViewDetails}
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
            />

            {/* Create/Edit Tenant Form */}
            <TenantForm
                open={showCreateForm || showEditForm}
                tenant={showEditForm && selectedTenant ? selectedTenant : undefined}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
                loading={formLoading}
                onValidateSubdomain={handleValidateSubdomain}
            />

            {/* Tenant Details Modal */}
            <TenantDetails
                open={showDetailsModal}
                tenant={selectedTenant}
                loading={detailsLoading}
                onClose={handleDetailsClose}
                onEdit={handleEditFromDetails}
            />

            {/* Delete Confirmation Dialog */}
            <TenantDeleteConfirmation
                open={showDeleteConfirmation}
                tenant={selectedTenant}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                loading={deleteLoading}
            />

            {/* Success/Error Notifications */}
            <Snackbar
                open={Boolean(notification)}
                autoHideDuration={notification?.duration || 6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification?.type || 'info'} sx={{ width: '100%' }}>
                    <AlertTitle>{notification?.title}</AlertTitle>
                    {notification?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
